import {forOwn, isObject, noop} from 'lodash';
import React from 'react';
import {Subject} from 'rxjs';

import {isDevelopment} from '../../../libs/shared/helpers/src/environment/envHelpers';
import {isMobile} from '../../../libs/shared/helpers/src/environment/mobileEnvHelpers';
import {CustomError, ExtendedError} from '../../../libs/shared/helpers/src/types/customErrors';
import Bugsnag from './bugsnagClient';
import {
  BugsnagReport,
  buildBugsnagReportingPipeline,
  findReportSeverity,
  onBugsnagError,
} from './bugsnagHelpers';
import {buildBugsnagOptions} from './bugsnagOptions';

/*
 * Constants.
 */

const isBugsnagEnabled = !isDevelopment();
const reports$ = new Subject<BugsnagReport>();

/*
 * Setup.
 */

export function setupBugsnag() {
  if (!isBugsnagEnabled) {
    return noop;
  }

  // Create the client.
  Bugsnag.start({
    ...buildBugsnagOptions(),

    onError: onBugsnagError,
  });

  if (!isMobile()) {
    // Start a session before the first location change, because we turned off auto-capturing.
    startBugsnagSession();
  }

  // Set up an RxJS pipeline that reports errors when online.
  const subscription = buildBugsnagReportingPipeline(reports$).subscribe((report) => sendToBugsnag(report));

  // Return unsubcribe function.
  return () => subscription.unsubscribe();
}

/*
 * Api.
 */

export function registerBugsnagUser(companyId: number, userId: number) {
  if (!isBugsnagEnabled) {
    return;
  }

  // Update the user on the client.
  Bugsnag.setUser(String(userId));
  Bugsnag.addMetadata('user', 'companyId', companyId);
}

export function reportToBugsnag(
  error: BugsnagReport['error'],
  options?: BugsnagReport['options'],
  callback?: BugsnagReport['callback'],
) {
  // Always log to the console.
  const serializableError = error instanceof CustomError && error.toSerializableObject();
  // eslint-disable-next-line no-console
  console.error(error, serializableError, options);

  if (isMobile() && isDevelopment() && error instanceof ExtendedError) {
    // Log box is not properly displaying ExtendedErrors so we need to display the original error
    // independently.
    // eslint-disable-next-line no-console
    console.error(error.originalError);
  }
  if (!isBugsnagEnabled) {
    // If Bugsnag is disabled, skip reporting.
    callback?.();
    return;
  }

  reports$.next({error, options, callback});
}

function sendToBugsnag(report: BugsnagReport) {
  const {error, options, callback} = report;
  const serializableError = error instanceof CustomError && error.toSerializableObject();

  Bugsnag.notify(
    error,
    (event) => {
      // eslint-disable-next-line no-param-reassign
      event.severity = options?.severity ?? findReportSeverity(event);
      event.addMetadata('Error details', serializableError || {...event.originalError});
      forOwn(options?.metadata || {}, (value, key) => {
        if (isObject(value)) {
          event.addMetadata(key, value);
        } else {
          event.addMetadata('custom', key, value);
        }
      });
    },
    callback,
  );
}

export function startBugsnagSession() {
  Bugsnag.startSession();
}

export function leaveBreadcrumb(message: string, metadata?: any, type?: string) {
  if (!isBugsnagEnabled) {
    return;
  }

  Bugsnag.leaveBreadcrumb(
    message,
    {
      ...metadata,
      eventType: type || '',
      date: new Date().toJSON(),
    },
    'log',
  );
}

export function leaveNavigationBreadcrumb(message: string, metadata: any) {
  if (!isBugsnagEnabled) {
    return;
  }

  Bugsnag.leaveBreadcrumb(
    message,
    {
      ...metadata,
      date: new Date().toJSON(),
    },
    'navigation',
  );
}

export function leaveStateBreadcrumb(message: string, metadata: any) {
  if (!isBugsnagEnabled) {
    return;
  }

  Bugsnag.leaveBreadcrumb(
    message,
    {
      ...metadata,
      date: new Date().toJSON(),
    },
    'state',
  );
}

export function buildMobileBugsnagErrorBoundary() {
  if (!isBugsnagEnabled) {
    // Using default React Native LogBox instead
    return undefined;
  }

  // Create a new react Error Boundary.
  return Bugsnag.getPlugin('react')?.createErrorBoundary(React);
}

/*
 * Types.
 */

export interface ErrorFallbackComponentProps {
  error: Error;
  info: React.ErrorInfo;
  clearError: () => void;
}
